import { useEffect, useMemo, useState } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import { Alert, Box, Button } from "@mui/material";
import { CalendarDialog } from "../../../components/CalendarDialog";
import { Autocomplete } from "../../../components/Autocomplete";
import { CalendarBox } from "./CalendarBox";
import { AlertDialog } from "../../../components/AlertDialog";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useToast } from "../../../hooks/useToast";
import {
  getInstances,
  getCalendarChatbot,
  postCalendarChatbot,
  putCalendarChatbot,
  postTriggerChatbot,
  putTriggerChatbot,
  deleteTriggerChatbot,
  getTriggersChatbot,
} from "../../../services/whatsapp";
import {
  CalendarObject,
  ChatbotDelTriggerObject,
  ChatbotPostPutCalendarObject,
  ChatbotPostPutTriggerObject,
  Gatilho,
  Instance,
} from "../../../types/whatsapp";
import DSAWS from "../../../services/DSAWS";

export function Chatbot() {
  const [instanceOnDialog, setInstanceOnDialog] = useState<Instance | null>(
    null
  );
  const [calendarOnDialog, setCalendarOnDialog] =
    useState<CalendarObject | null>(null);
  const [openCalendarOnDialog, setOpenCalendarDialog] =
    useState<boolean>(false);
  const [getTriggers, setGetTriggers] = useState<Gatilho[] | null>(null);
  const [openDeleteCalendarDialog, setOpenDeleteCalendarDialog] =
    useState<boolean>(false);

  const toast = useToast();
  const queryClient = useQueryClient();

  const instances = useQuery("instances", getInstances);

  const calendars = useQuery(
    ["calendars", instanceOnDialog?.id],
    () => getCalendarChatbot(instanceOnDialog?.id!),
    {
      enabled: Boolean(instanceOnDialog?.id),
    }
  );

  const {
    mutateAsync: mutatePutCalendar,
    isLoading: loadingMutatePutCalendar,
  } = useMutation(putCalendarChatbot);

  const { mutateAsync: mutatePostCalendar, isLoading: loadingPostCalendar } =
    useMutation(postCalendarChatbot);

  const { isLoading: loadingGetTriggers, mutateAsync: mutateGetTriggers } =
    useMutation("getTriggerChatbot", getTriggersChatbot, {
      onSuccess(data) {
        setGetTriggers(data);
      },
    });

  const { mutateAsync: mutatePutTrigger, isLoading: loadingPutTrigger } =
    useMutation(putTriggerChatbot);

  const { mutateAsync: mutatePostTrigger, isLoading: loadingPostTrigger } =
    useMutation(postTriggerChatbot);

  const { mutateAsync: mutateDeleteTrigger } = useMutation(
    deleteTriggerChatbot,
    {
      onSuccess() {
        toast.show("Gatilho deletado com sucesso", { severity: "success" });
      },
    }
  );

  const handleCalendarDialog = (
    action = false,
    calendar: CalendarObject | null = null
  ) => {
    if (!action) {
      setGetTriggers(null);
    }
    setCalendarOnDialog(calendar);
    setOpenCalendarDialog(action);
  };

  const handleSaveCalendar = async (
    calendar: ChatbotPostPutCalendarObject,
    gatilhos: Gatilho[]
  ) => {
    handleCalendarDialog();
    let responseCalendar: any = {};
    let trigger: ChatbotPostPutTriggerObject = { message: "", url: "" };
    if (calendar.calendar_id) {
      toast.show("Atualizando sua resposta automática", { severity: "info" });
      responseCalendar = await mutatePutCalendar(calendar);
    } else {
      toast.show("Criando sua resposta automática", { severity: "info" });
      responseCalendar = await mutatePostCalendar(calendar);
    }
    for (const gatilho of gatilhos) {
      let s3Response: any = {};
      if (gatilho.url && gatilho.url !== null) {
        s3Response = await DSAWS.saveS3({
          folderName: "chatbot",
          file: gatilho.url,
          type: gatilho.url?.type,
          extension: gatilho.url?.name?.split(".")[1],
        });
      } else if (gatilho.url === null) {
        s3Response.Location = null;
      }
      trigger = {
        ...(gatilho.id && { trigger_id: gatilho.id }),
        ...((responseCalendar.calendar_id || calendar.calendar_id) && {
          calendar_id: responseCalendar.calendar_id || calendar.calendar_id,
        }),
        url: s3Response.Location,
        message: gatilho.mensagem,
      };
      gatilho.id
        ? await mutatePutTrigger(trigger)
        : await mutatePostTrigger(trigger);
      toast.show("Processo realizado com sucesso", { severity: "success" });
      await queryClient.invalidateQueries(["calendars", instanceOnDialog?.id]);
    }
  };

  const handleDeleteCalendar = async () => {
    toast.show("Deletando o seu calendario.", { severity: "info" });
    if (calendarOnDialog) {
      const response = await mutatePutCalendar({
        active: !calendarOnDialog.ativo,
        calendar_id: calendarOnDialog.id,
      });
      if (response?.message.includes("successfully")) {
        toast.show("Calendario deletado com sucesso.", { severity: "success" });
      }
    }
    handleCalendarDialog();
    await queryClient.invalidateQueries(["calendars", instanceOnDialog?.id]);
  };

  const handleOpenDeleteCalendarDialog = (action = false) => {
    setOpenDeleteCalendarDialog(action);
  };

  const handleInstanceOnDialog = (instance = null) => {
    setInstanceOnDialog(instance);
  };

  const isLoading = useMemo(() => {
    return (
      loadingMutatePutCalendar ||
      loadingPostCalendar ||
      calendars.isLoading ||
      loadingPutTrigger ||
      loadingPostTrigger
    );
  }, [
    loadingMutatePutCalendar,
    loadingPostCalendar,
    calendars.isLoading,
    loadingPutTrigger,
    loadingPostTrigger,
  ]);

  useEffect(() => {
    handleCalendarDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceOnDialog]);

  useEffect(() => {
    if (instances.data?.length) {
      setInstanceOnDialog(instances.data[0]);
    }
  }, [instances.data]);

  return (
    <Box>
      <Box
        px={"2.5rem"}
        display={"flex"}
        flexDirection={"row"}
        gap={"1.5rem"}
        mb={3}
        justifyContent={"space-between"}
      >
        <Box>
          <Autocomplete
            label={"Instância de Whatsapp"}
            placeholder={"Instância de Whatsapp"}
            value={instanceOnDialog}
            defaultValue={instances.data?.length && instances.data[0]}
            options={instances.data || []}
            getOptionLabel={(instance) => instance.nome}
            sx={{ width: "33.75rem", height: "3rem" }}
            onChange={(_, instance) => {
              handleInstanceOnDialog(instance);
            }}
            loading={instances.isLoading}
          />
          <Typography
            mt={2}
            fontWeight={500}
            fontSize={"0.75rem"}
            lineHeight={"0.9375rem"}
            color={"#2A2A2B"}
          >
            Escolha o número de whatsapp que deseja configurar
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => {
            handleCalendarDialog(true);
          }}
          sx={{ maxWidth: "300px", height: "2.25rem" }}
          disabled={isLoading || !instanceOnDialog?.id}
        >
          Cadastrar nova palavra-chave
        </Button>
      </Box>
      <Box>
        {Boolean(instances.data?.length && !instanceOnDialog) && (
          <Alert severity="info">Selecione uma instância.</Alert>
        )}
        {isLoading ? (
          <Stack
            spacing={2}
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
          >
            <Typography variant="subtitle1">Buscando informações</Typography>
            <LinearProgress variant="indeterminate" sx={{ width: "25%" }} />
          </Stack>
        ) : (
          Boolean(calendars.data?.length) &&
          calendars.data?.map((calendar: CalendarObject, index: number) => (
            <CalendarBox
              key={index}
              calendar={calendar}
              onEdit={() => {
                handleCalendarDialog(true, calendar);
                mutateGetTriggers({ calendar_id: calendar.id });
              }}
              onDelete={() => {
                handleCalendarDialog(false, calendar);
                handleOpenDeleteCalendarDialog(true);
              }}
            />
          ))
        )}
        {Boolean(
          !calendars.data?.length &&
            instanceOnDialog &&
            !calendars.isLoading &&
            !isLoading
        ) && (
          <Alert severity="info">
            Nenhuma resposta automática encontrada para a intância selecionada
          </Alert>
        )}
      </Box>

      {openCalendarOnDialog && (
        <CalendarDialog
          open={openCalendarOnDialog}
          calendario={calendarOnDialog}
          instances={instances.data as []}
          gatilhos={getTriggers}
          instanceSelected={instanceOnDialog}
          hasTranshipment={false}
          loading={false}
          loadingGatilhos={loadingGetTriggers}
          onClose={() => handleCalendarDialog()}
          onSave={(
            calendar: ChatbotPostPutCalendarObject,
            gatilhos: Gatilho[]
          ) => {
            handleSaveCalendar(calendar, gatilhos);
          }}
          onDelGatilho={(trigger: ChatbotDelTriggerObject) => {
            mutateDeleteTrigger(trigger);
          }}
        />
      )}
      {openDeleteCalendarDialog && (
        <AlertDialog
          open={openDeleteCalendarDialog}
          title="Deseja realmente excluir esse item?"
          textAgree="Sim"
          handleAgree={() => {
            handleOpenDeleteCalendarDialog();
            handleDeleteCalendar();
          }}
          handleClose={() => handleOpenDeleteCalendarDialog()}
        />
      )}
    </Box>
  );
}
